import React, { useRef, useLayoutEffect } from 'react'
import PopoverContent from './Content'
import Popper, { PopperChildrenProps } from '../Popper'
import { Placement, Instance } from '@popperjs/core'
import useOnClickOutside from 'utils/hooks/useOnClickOutside'
import { Modifier } from 'react-popper'

export type PopoverChildrenProps =
  | ((props: PopperChildrenProps) => JSX.Element)
  | JSX.Element

export interface PopoverProps {
  children: PopoverChildrenProps
  popup: JSX.Element
  contentPadding?: string
  fillContent?: boolean
  visible?: boolean
  spacing?: string
  placement?: Placement
  modifiers?: Modifier<any>[]
  contentStyle?: React.CSSProperties
  [propName: string]: any
  onClickOutside?: (e: MouseEvent) => void
  usePortal?: boolean
}

type ForceUpdate = Instance['forceUpdate'] | null

const accessibility = (visible: boolean) => ({
  'aria-haspopup': 'true',
  'aria-expanded': `${visible}`,
})

/**
 *
 * The Popover can be used instead of the Tooltip when you want more control in styling / content you want to display.
 *
 * It can have different placements, you can check all of them via the Properties table controls on Storybook.
 *
 */
const Popover = ({
  popup,
  children,
  contentPadding = '0px',
  visible = false,
  spacing = '0px',
  fillContent = false,
  onClickOutside,
  usePortal = true,
  ...restProps
}: PopoverProps) => {
  const contentRef = useRef(null)
  const popoverRef = useRef(null)

  const updateRef = useRef<ForceUpdate>(null)

  useLayoutEffect(() => {
    updateRef.current?.()
  }, [popup])

  useOnClickOutside(
    contentRef,
    e => {
      onClickOutside?.(e)
    },
    { includeRefs: [popoverRef] }
  )

  return (
    <Popper
      spacing="0px"
      visible={visible}
      usePortal={usePortal}
      {...accessibility(visible)}
      {...restProps}
      popup={({ rect, ...popperProps }) => {
        const update = updateRef as React.MutableRefObject<ForceUpdate>
        update.current = popperProps.forceUpdate

        return (
          <span ref={popoverRef}>
            <PopoverContent
              fillContent={fillContent}
              fillWidth={rect ? `${rect.width}px` : undefined}
              contentPadding={contentPadding}
              data-testid="popover-content"
              {...popperProps}>
              {popup}
            </PopoverContent>
          </span>
        )
      }}>
      <div ref={contentRef}>{children}</div>
    </Popper>
  )
}

export default Popover
